import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Autocomplete
} from "@mui/material";
import countryList from "../../utils/countries"; // adjust path if needed

const CreateBuddy = ({ open, onClose, parentEmail, onCreate }) => {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: parentEmail,
        company: "",
        country: null,
    });
    const [emailError, setEmailError] = useState("");

    // Keep form.email in sync with the parent
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            email: parentEmail
        }));
    }, [parentEmail]);

    // Prefill firstName and lastName from email if possible
    useEffect(() => {
        if (!parentEmail) return;

        if (!parentEmail.endsWith("@meltwater.com")) {
            setEmailError("We can only create buddy accounts for @meltwater.com emails.");
            return;
        }

        setEmailError(""); // clear any previous error

        const namePart = parentEmail.split("@")[0]; // 'firstname.lastname'
        const [first, last] = namePart.split(".");
        setForm((prev) => ({
            ...prev,
            firstName: first ? capitalize(first) : "",
            lastName: last ? capitalize(last) : "",
        }));
    }, [parentEmail]);

    // Auto-generate company name when first or last name changes
    useEffect(() => {
        if (form.firstName || form.lastName) {
            const autoName = `${form.firstName}${form.lastName ? " " + form.lastName : ""} - Buddy`.trim();
            setForm((prev) => ({ ...prev, company: autoName }));
        }
    }, [form.firstName, form.lastName]);

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onCreate(form);
        onClose();
    };

    const isCreateDisabled = !form.firstName || !form.lastName || !form.country || !!emailError;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Buddy</DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                        Please provide the details for the new buddy account. This will also assign the default entitlements.
                        If you need to customize entitlements, you can do so after creating the account.
                    </Typography>

                    <TextField
                        label="User Email"
                        value={parentEmail}
                        fullWidth
                        disabled
                        error={!!emailError}
                        helperText={emailError}
                    />

                    <TextField
                        label="First Name"
                        value={form.firstName}
                        fullWidth
                        onChange={(e) => handleChange("firstName", e.target.value)}
                        disabled={!!emailError}
                    />

                    <TextField
                        label="Last Name"
                        value={form.lastName}
                        fullWidth
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        disabled={!!emailError}
                    />

                    <TextField
                        label="Company Name"
                        value={form.company}
                        fullWidth
                        onChange={(e) => handleChange("company", e.target.value)}
                        helperText="Auto-generated but editable"
                        disabled={!!emailError}
                    />

                    <Autocomplete
                        options={countryList}
                        getOptionLabel={(option) => option.name}
                        value={form.country}
                        onChange={(event, newValue) => handleChange("country", newValue)}
                        renderInput={(params) => <TextField {...params} label="Country" fullWidth />}
                        isOptionEqualToValue={(option, value) => option.val === value?.val}
                        disableClearable
                        disabled={!!emailError}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isCreateDisabled}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>

    );
};

export default CreateBuddy;
